import React, {useState, useEffect} from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Collapse,
  Alert,
  CircularProgress,
  useTheme,
  Tooltip,
  Menu,
  MenuList,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material'
import {
  TableChart as TableIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  ContentCopy as CloneIcon,
  AutoFixHigh as MockDataIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import MockDataGeneratorDialog from './MockDataGeneratorDialog'
import {config} from '../../config/environment'

interface TableInfo {
  name: string
  type: 'table' | 'view'
  sql: string
  rowCount: number
  columns: Array<{
    name: string
    type: string
    notNull: boolean
    primaryKey: boolean
    defaultValue: any
  }>
}

interface TableData {
  data: any[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface TablesManagerProps {
  onError: (error: string | null) => void
  refreshTrigger: boolean
}

/**
 * Tables Manager Component
 * Provides comprehensive table browsing and data editing capabilities
 */ const TablesManager: React.FC<TablesManagerProps> = ({
  onError,
  refreshTrigger
}) => {
  const theme = useTheme()
  const [tables, setTables] = useState<TableInfo[]>([])
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [tableData, setTableData] = useState<TableData | null>(null)
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [editDialog, setEditDialog] = useState<{
    open: boolean
    mode: 'add' | 'edit' | 'clone'
    rowData?: any
    rowIndex?: number
  }>({open: false, mode: 'add'})
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    rowData?: any
    rowIndex?: number
  }>({open: false})
  const [actionMenu, setActionMenu] = useState<{
    anchor: HTMLElement | null
    tableName: string | null
  }>({anchor: null, tableName: null})
  const [mockDataDialog, setMockDataDialog] = useState<{
    open: boolean
    tableName: string | null
  }>({open: false, tableName: null})

  // Get API base URL
  const getApiBaseUrl = () => {
    return config.API_BASE_URL
  }

  const fetchTables = async () => {
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) {
        onError('Authentication token not found')
        return
      }

      const response = await fetch(`${getApiBaseUrl()}/api/db-admin/tables`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch tables')
      }

      // Debug: Log the analytics_events table data
      const analyticsTable = data.data.find(
        (table: any) => table.name === 'analytics_events'
      )
      if (analyticsTable) {
        console.log('🔍 Analytics Events Table Debug:', {
          name: analyticsTable.name,
          columnCount: analyticsTable.columns?.length || 0,
          columns: analyticsTable.columns?.map((col: any) => col.name) || []
        })
      }

      setTables(data.data)
    } catch (error) {
      console.error('Error fetching tables:', error)
      onError(error instanceof Error ? error.message : 'Failed to fetch tables')
    }
  }

  const fetchTableData = async (tableName: string, currentPage: number = 0) => {
    setIsLoadingData(true)
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) return

      const response = await fetch(
        `${getApiBaseUrl()}/api/db-admin/tables/${encodeURIComponent(
          tableName
        )}/data?page=${currentPage + 1}&pageSize=${rowsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch table data')
      }

      setTableData(data.data)
      setPage(currentPage)
    } catch (error) {
      console.error('Error fetching table data:', error)
      onError(
        error instanceof Error ? error.message : 'Failed to fetch table data'
      )
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleTableClick = (tableName: string) => {
    setSelectedTable(tableName)
    fetchTableData(tableName, 0)
  }

  const handleTableExpand = (tableName: string) => {
    const newExpanded = new Set(expandedTables)
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName)
    } else {
      newExpanded.add(tableName)
    }
    setExpandedTables(newExpanded)
  }

  const handlePageChange = (event: unknown, newPage: number) => {
    if (selectedTable) {
      fetchTableData(selectedTable, newPage)
    }
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    if (selectedTable) {
      fetchTableData(selectedTable, 0)
    }
  }

  const handleExport = async (tableName: string, format: 'json' | 'csv') => {
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) return

      const response = await fetch(
        `${getApiBaseUrl()}/api/db-admin/tables/${encodeURIComponent(
          tableName
        )}/export?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Export failed')
      }

      // Trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${tableName}.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Export failed')
    }
  }

  const handleDeleteRow = async (rowData: any) => {
    if (!selectedTable || !selectedTableInfo) return

    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) return

      // Build WHERE clause using primary key columns
      const primaryKeys = selectedTableInfo.columns.filter(
        (col) => col.primaryKey
      )
      if (primaryKeys.length === 0) {
        onError('Cannot delete row: No primary key found')
        return
      }

      const whereClause = primaryKeys
        .map((pk) => `"${pk.name}" = ?`)
        .join(' AND ')
      const whereValues = primaryKeys.map((pk) => rowData[pk.name])

      const response = await fetch(
        `${getApiBaseUrl()}/api/db-admin/tables/${encodeURIComponent(
          selectedTable
        )}/rows`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            where: whereClause,
            whereValues: whereValues
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete row')
      }

      // Refresh table data
      fetchTableData(selectedTable, page)
      setDeleteDialog({open: false})
    } catch (error) {
      console.error('Delete row error:', error)
      onError(error instanceof Error ? error.message : 'Failed to delete row')
    }
  }

  const loadData = async () => {
    setIsLoading(true)
    onError(null)

    try {
      await fetchTables()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [refreshTrigger])

  const selectedTableInfo = tables.find((t) => t.name === selectedTable)

  // Debug: Log selected table info
  React.useEffect(() => {
    if (selectedTableInfo && selectedTable === 'analytics_events') {
      console.log('🔍 Selected Analytics Table Debug:', {
        name: selectedTableInfo.name,
        columnCount: selectedTableInfo.columns?.length || 0,
        columns: selectedTableInfo.columns?.map((col) => col.name) || []
      })
    }
  }, [selectedTableInfo, selectedTable])

  return (
    <Grid container spacing={3}>
      {/* Tables List */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Typography variant="h6" component="h3">
                Database Tables
              </Typography>
              <IconButton onClick={loadData} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {isLoading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress />
              </Box>
            ) : tables.length > 0 ? (
              <Box>
                {tables.map((table) => (
                  <Card
                    key={table.name}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      bgcolor:
                        selectedTable === table.name
                          ? 'action.selected'
                          : 'background.paper',
                      '&:hover': {bgcolor: 'action.hover'}
                    }}
                    onClick={() => handleTableClick(table.name)}
                  >
                    <CardContent sx={{py: 1}}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexGrow: 1
                          }}
                        >
                          {table.type === 'table' ? (
                            <TableIcon color="primary" />
                          ) : (
                            <ViewIcon color="secondary" />
                          )}
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {table.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {table.rowCount.toLocaleString()} rows
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{display: 'flex', alignItems: 'center', gap: 1}}
                        >
                          <Chip
                            size="small"
                            label={table.type}
                            variant="outlined"
                            color={
                              table.type === 'table' ? 'primary' : 'secondary'
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleTableExpand(table.name)
                            }}
                          >
                            {expandedTables.has(table.name) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setActionMenu({
                                anchor: e.currentTarget,
                                tableName: table.name
                              })
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={expandedTables.has(table.name)}>
                        <Box sx={{mt: 2, pl: 2}}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{mb: 1, display: 'block'}}
                          >
                            Columns ({table.columns.length}):
                          </Typography>
                          <Box sx={{mt: 1}}>
                            {table.columns.map((column) => (
                              <Box
                                key={column.name}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  bgcolor: 'action.hover',
                                  borderRadius: 1
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.5
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {column.name}
                                  </Typography>
                                  <Box sx={{display: 'flex', gap: 0.5}}>
                                    {column.primaryKey && (
                                      <Chip
                                        size="small"
                                        label="PK"
                                        color="primary"
                                        variant="outlined"
                                        sx={{height: 18, fontSize: '0.6rem'}}
                                      />
                                    )}
                                    {column.notNull && (
                                      <Chip
                                        size="small"
                                        label="NOT NULL"
                                        color="warning"
                                        variant="outlined"
                                        sx={{height: 18, fontSize: '0.6rem'}}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{display: 'block'}}
                                >
                                  Type:{' '}
                                  <Box component="span" fontWeight="medium">
                                    {column.type}
                                  </Box>
                                </Typography>
                                {column.defaultValue !== null &&
                                  column.defaultValue !== undefined && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{display: 'block'}}
                                    >
                                      Default:{' '}
                                      <Box component="span" fontWeight="medium">
                                        {String(column.defaultValue)}
                                      </Box>
                                    </Typography>
                                  )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{textAlign: 'center', py: 4}}
              >
                No tables found in database
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Table Data Viewer */}
      <Grid item xs={12} md={8}>
        {selectedTable && selectedTableInfo ? (
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3
                }}
              >
                <Box>
                  <Typography variant="h6" component="h3">
                    {selectedTable}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTableInfo.rowCount.toLocaleString()} total rows •{' '}
                    {selectedTableInfo.columns.length} columns
                  </Typography>
                </Box>
                <Box sx={{display: 'flex', gap: 1}}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setEditDialog({open: true, mode: 'add'})}
                    disabled={selectedTableInfo.type === 'view'}
                  >
                    Add Row
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchTableData(selectedTable, page)}
                    disabled={isLoadingData}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {isLoadingData ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                  <CircularProgress />
                </Box>
              ) : tableData && tableData.data.length > 0 ? (
                <>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{maxWidth: '100%', overflowX: 'auto'}}
                  >
                    <Table size="small" stickyHeader sx={{minWidth: 1200}}>
                      <TableHead>
                        <TableRow>
                          {selectedTableInfo.columns.map((column, index) => (
                            <TableCell
                              key={column.name}
                              sx={{minWidth: 120, whiteSpace: 'nowrap'}}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {column.name}
                                </Typography>
                                {column.primaryKey && (
                                  <Chip
                                    size="small"
                                    label="PK"
                                    color="primary"
                                    variant="outlined"
                                    sx={{height: 20}}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {column.type}
                              </Typography>
                            </TableCell>
                          ))}
                          {selectedTableInfo.type === 'table' && (
                            <TableCell
                              width={140}
                              sx={{
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'background.paper'
                              }}
                            >
                              Actions
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.data.map((row, index) => (
                          <TableRow key={index} hover>
                            {selectedTableInfo.columns.map(
                              (column, colIndex) => (
                                <TableCell
                                  key={column.name}
                                  sx={{minWidth: 120, maxWidth: 200}}
                                >
                                  {row[column.name] === null ? (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontStyle="italic"
                                    >
                                      NULL
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {String(row[column.name])}
                                    </Typography>
                                  )}
                                </TableCell>
                              )
                            )}
                            {selectedTableInfo.type === 'table' && (
                              <TableCell
                                sx={{
                                  position: 'sticky',
                                  right: 0,
                                  bgcolor: 'background.paper'
                                }}
                              >
                                <Box sx={{display: 'flex', gap: 0.5}}>
                                  <Tooltip title="Edit Row">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setEditDialog({
                                          open: true,
                                          mode: 'edit',
                                          rowData: row,
                                          rowIndex: index
                                        })
                                      }
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Clone Row">
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() =>
                                        setEditDialog({
                                          open: true,
                                          mode: 'clone',
                                          rowData: row,
                                          rowIndex: index
                                        })
                                      }
                                    >
                                      <CloneIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Row">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        setDeleteDialog({
                                          open: true,
                                          rowData: row,
                                          rowIndex: index
                                        })
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={tableData.total}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[25, 50, 100]}
                    showFirstButton
                    showLastButton
                  />
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{textAlign: 'center', py: 4}}
                >
                  No data found in this table
                </Typography>
              )}
            </CardContent>
          </Card>
        ) : (
          <Paper sx={{p: 4, textAlign: 'center'}}>
            <TableIcon sx={{fontSize: 64, color: 'text.secondary', mb: 2}} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select a Table
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a table from the list on the left to view and edit its data
            </Typography>
          </Paper>
        )}
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenu.anchor}
        open={Boolean(actionMenu.anchor)}
        onClose={() => setActionMenu({anchor: null, tableName: null})}
      >
        <MenuList>
          <MenuItem
            onClick={() => {
              if (actionMenu.tableName) {
                const tableInfo = tables.find(
                  (t) => t.name === actionMenu.tableName
                )
                if (tableInfo?.type === 'table') {
                  setMockDataDialog({
                    open: true,
                    tableName: actionMenu.tableName
                  })
                }
              }
              setActionMenu({anchor: null, tableName: null})
            }}
            disabled={
              !actionMenu.tableName ||
              tables.find((t) => t.name === actionMenu.tableName)?.type ===
                'view'
            }
          >
            <ListItemIcon>
              <MockDataIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Generate Mock Data</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              if (actionMenu.tableName) {
                handleExport(actionMenu.tableName, 'json')
              }
              setActionMenu({anchor: null, tableName: null})
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as JSON</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionMenu.tableName) {
                handleExport(actionMenu.tableName, 'csv')
              }
              setActionMenu({anchor: null, tableName: null})
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Add/Edit Row Dialog */}
      <RowEditDialog
        open={editDialog.open}
        mode={editDialog.mode}
        tableName={selectedTable}
        tableColumns={selectedTableInfo?.columns || []}
        rowData={editDialog.rowData}
        onClose={() => setEditDialog({open: false, mode: 'add'})}
        onSave={(data) => {
          // Handle save logic here
          console.log('Save row data:', data)
          setEditDialog({open: false, mode: 'add'})
          // Refresh table data
          if (selectedTable) {
            fetchTableData(selectedTable, page)
          }
        }}
        onError={onError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({open: false})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{pb: 1}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{mb: 2}}>
            <Typography variant="body2">
              This action cannot be undone. The row will be permanently deleted
              from the database.
            </Typography>
          </Alert>

          {deleteDialog.rowData && selectedTableInfo && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Row details:
              </Typography>
              <Paper variant="outlined" sx={{p: 2, bgcolor: 'grey.50'}}>
                {selectedTableInfo.columns.slice(0, 3).map((column) => (
                  <Typography key={column.name} variant="body2" sx={{mb: 0.5}}>
                    <strong>{column.name}:</strong>{' '}
                    {String(deleteDialog.rowData[column.name] || 'NULL')}
                  </Typography>
                ))}
                {selectedTableInfo.columns.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {selectedTableInfo.columns.length - 3} more fields
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({open: false})}>Cancel</Button>
          <Button
            onClick={() => handleDeleteRow(deleteDialog.rowData)}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Row
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mock Data Generator Dialog */}
      <MockDataGeneratorDialog
        open={mockDataDialog.open}
        onClose={() => setMockDataDialog({open: false, tableName: null})}
        tableName={mockDataDialog.tableName}
        tableColumns={
          mockDataDialog.tableName
            ? tables.find((t) => t.name === mockDataDialog.tableName)
                ?.columns || []
            : []
        }
        onSuccess={() => {
          // Refresh table data if currently viewing the table
          if (selectedTable === mockDataDialog.tableName && selectedTable) {
            fetchTableData(selectedTable, page)
          }
          // Refresh tables list to update row counts
          fetchTables()
        }}
        onError={onError}
      />
    </Grid>
  )
}

// Row Edit Dialog Component
interface RowEditDialogProps {
  open: boolean
  mode: 'add' | 'edit' | 'clone'
  tableName: string | null
  tableColumns: Array<{
    name: string
    type: string
    notNull: boolean
    primaryKey: boolean
    defaultValue: any
  }>
  rowData?: any
  onClose: () => void
  onSave: (data: Record<string, any>) => void
  onError: (error: string | null) => void
}

const RowEditDialog: React.FC<RowEditDialogProps> = ({
  open,
  mode,
  tableName,
  tableColumns,
  rowData,
  onClose,
  onSave,
  onError
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(false)

  // Get API base URL
  const getApiBaseUrl = () => {
    return (
      (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) ||
      'http://localhost:3000'
    )
  }

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && rowData) {
        setFormData({...rowData})
      } else if (mode === 'clone' && rowData) {
        // Clone data but clear primary key fields
        const clonedData = {...rowData}
        tableColumns.forEach((column) => {
          if (column.primaryKey) {
            // Clear primary key fields for cloning
            clonedData[column.name] = ''
          }
        })
        setFormData(clonedData)
      } else {
        // Initialize with default values for add mode
        const initialData: Record<string, any> = {}
        tableColumns.forEach((column) => {
          if (
            column.defaultValue !== null &&
            column.defaultValue !== undefined
          ) {
            initialData[column.name] = column.defaultValue
          } else {
            initialData[column.name] = ''
          }
        })
        setFormData(initialData)
      }
    }
  }, [open, mode, rowData, tableColumns])

  const handleSave = async () => {
    if (!tableName) return

    setIsLoading(true)
    onError(null)

    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) {
        onError('Authentication token not found')
        return
      }

      if (mode === 'add' || mode === 'clone') {
        // Insert new row
        const response = await fetch(
          `${getApiBaseUrl()}/api/db-admin/tables/${encodeURIComponent(
            tableName
          )}/rows`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({data: formData})
          }
        )

        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.error || 'Failed to insert row')
        }

        onSave(formData)
      } else if (mode === 'edit') {
        // Update existing row
        const primaryKeys = tableColumns.filter((col) => col.primaryKey)
        if (primaryKeys.length === 0) {
          onError('Cannot update row: No primary key found')
          return
        }

        const whereClause = primaryKeys
          .map((pk) => `"${pk.name}" = ?`)
          .join(' AND ')
        const whereValues = primaryKeys.map((pk) => rowData[pk.name])

        const response = await fetch(
          `${getApiBaseUrl()}/api/db-admin/tables/${encodeURIComponent(
            tableName
          )}/rows`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              data: formData,
              where: whereClause,
              whereValues: whereValues
            })
          }
        )

        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.error || 'Failed to update row')
        }

        onSave(formData)
      }
    } catch (error) {
      console.error('Save row error:', error)
      onError(error instanceof Error ? error.message : 'Failed to save row')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldChange = (columnName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [columnName]: value
    }))
  }

  const getInputType = (columnType: string): string => {
    const type = columnType.toLowerCase()
    if (
      type.includes('int') ||
      type.includes('real') ||
      type.includes('float') ||
      type.includes('double')
    ) {
      return 'number'
    }
    if (type.includes('date') || type.includes('time')) {
      return 'datetime-local'
    }
    return 'text'
  }

  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New Row'
      case 'edit':
        return 'Edit Row'
      case 'clone':
        return 'Clone Row'
      default:
        return 'Row Editor'
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {mode === 'add' && <AddIcon color="primary" />}
          {mode === 'edit' && <EditIcon color="primary" />}
          {mode === 'clone' && <CloneIcon color="info" />}
          <Typography variant="h6">
            {getDialogTitle()} - {tableName}
          </Typography>
        </Box>
        {mode === 'clone' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{display: 'block', mt: 0.5}}
          >
            Creating a copy of the selected row with new primary key values
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{pt: 1}}>
          <Grid container spacing={2}>
            {tableColumns.map((column) => (
              <Grid item xs={12} sm={6} key={column.name}>
                <TextField
                  fullWidth
                  label={column.name}
                  type={getInputType(column.type)}
                  value={formData[column.name] || ''}
                  onChange={(e) =>
                    handleFieldChange(column.name, e.target.value)
                  }
                  required={column.notNull && !column.defaultValue}
                  disabled={column.primaryKey && mode === 'edit'}
                  helperText={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {column.type}
                        {column.primaryKey && ' • Primary Key'}
                        {column.notNull && ' • Required'}
                        {column.defaultValue &&
                          ` • Default: ${column.defaultValue}`}
                      </Typography>
                    </Box>
                  }
                  variant="outlined"
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} />
            ) : mode === 'add' ? (
              <AddIcon />
            ) : mode === 'edit' ? (
              <EditIcon />
            ) : (
              <CloneIcon />
            )
          }
        >
          {isLoading
            ? 'Saving...'
            : mode === 'add'
            ? 'Add Row'
            : mode === 'edit'
            ? 'Update Row'
            : 'Clone Row'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TablesManager
